/**
 * Country Slice - Global state for country detection
 * 
 * Stores the detected country code and provides actions to update it.
 * Used by useCountryDetection hook and CountryComponentProvider.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    DEFAULT_COUNTRY,
    COUNTRY_CONFIGS,
    getCountryConfigOrDefault,
    type CountryConfig
} from '../config/globalConfig';

interface CountryState {
    countryCode: string;
    detectionSource: 'subdomain' | 'userProfile' | 'urlParam' | 'localStorage' | 'language' | 'default';
}

const initialState: CountryState = {
    countryCode: DEFAULT_COUNTRY,
    detectionSource: 'default',
};

const countrySlice = createSlice({
    name: 'country',
    initialState,
    reducers: {
        setCountryCode: (
            state,
            action: PayloadAction<{ code: string; source: CountryState['detectionSource'] }>
        ) => {
            const { code, source } = action.payload;
            console.log('[countrySlice] setCountryCode called:', { code, source, isValid: !!COUNTRY_CONFIGS[code] });
            // Only set if valid country code
            if (COUNTRY_CONFIGS[code]) {
                state.countryCode = code;
                state.detectionSource = source;
                // Persist to localStorage so country survives page reloads
                try {
                    localStorage.setItem('proflow_country', code);
                    console.log('[countrySlice] Persisted country to localStorage:', code);
                } catch (e) {
                    // localStorage may be unavailable (private browsing, etc.)
                }
                console.log('[countrySlice] State updated to:', { countryCode: code, source });
            }
        },
        resetCountry: (state) => {
            state.countryCode = DEFAULT_COUNTRY;
            state.detectionSource = 'default';
        },
    },
});

export const { setCountryCode, resetCountry } = countrySlice.actions;

// Selectors
export const selectCountryCode = (state: { country: CountryState }) =>
    state.country.countryCode;

export const selectCountryConfig = (state: { country: CountryState }): CountryConfig =>
    getCountryConfigOrDefault(state.country.countryCode);

export const selectDetectionSource = (state: { country: CountryState }) =>
    state.country.detectionSource;

export default countrySlice.reducer;
